import { useCallback, useEffect, useRef } from 'react';
import useAuth from 'src/hooks/useAuth';

const DEFAULT_INTERVAL_MS = 3600000; // 1 hour
const BACKOFF_INTERVAL_MS = 7200000; // 2 hours after consecutive errors

const useSystemAlertFetcher = ({ sources, onFetchSuccess, onFetchFailure, onLoading, pollingEnabled = false }) => {
  const { isLoggedIn } = useAuth();
  const consecutiveErrorsRef = useRef(0);
  const sourcesRef = useRef(sources);
  const abortRef = useRef(null);
  const intervalRef = useRef(null);
  const pollIntervalRef = useRef(DEFAULT_INTERVAL_MS);
  const hasFetchedRef = useRef(false);

  useEffect(() => {
    sourcesRef.current = sources;
  }, [sources]);

  const handleSource = async (source, signal) => {
    const res = await source.fetch(signal);
    const transformed = source.transform ? source.transform(res) : res;
    return transformed;
  };

  const clearIntervalIfAny = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const handleFetchFailure = useCallback(() => {
    if (pollingEnabled) {
      const next = consecutiveErrorsRef.current + 1;
      consecutiveErrorsRef.current = next;
      if (next >= 3 && pollIntervalRef.current !== BACKOFF_INTERVAL_MS) {
        pollIntervalRef.current = BACKOFF_INTERVAL_MS;
      }
    }
    onFetchFailure();
  }, [pollingEnabled, onFetchFailure]);

  const refreshAlerts = useCallback(async () => {
    if (!isLoggedIn) return;

    if (abortRef.current) {
      try {
        abortRef.current.abort();
      } catch (_) {}
    }

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      if (onLoading) onLoading(true);

      const results = await Promise.allSettled(
        (sourcesRef.current || []).map(async (source) => {
          return await handleSource(source, controller.signal);
        })
      );

      const collected = [];
      let hasAtLeastOneSuccess = false;

      results.forEach((res) => {
        if (res.status === 'fulfilled') {
          hasAtLeastOneSuccess = true;
          if (!res.value) return;
          collected.push(res.value);
        }
      });

      if (!hasAtLeastOneSuccess || collected.length === 0) {
        handleFetchFailure();
        return;
      }

      consecutiveErrorsRef.current = 0;
      if (pollingEnabled && pollIntervalRef.current !== DEFAULT_INTERVAL_MS) {
        pollIntervalRef.current = DEFAULT_INTERVAL_MS;
      }

      onFetchSuccess(collected);
    } catch (_) {
      handleFetchFailure();
    } finally {
      if (onLoading) onLoading(false);
    }
  }, [isLoggedIn, onFetchSuccess, handleFetchFailure, onLoading, pollingEnabled]);

  const startPolling = useCallback(() => {
    clearIntervalIfAny();
    refreshAlerts();
    intervalRef.current = setInterval(() => {
      refreshAlerts();
    }, pollIntervalRef.current);
  }, [clearIntervalIfAny, refreshAlerts]);

  const stopPolling = useCallback(() => {
    clearIntervalIfAny();
    if (abortRef.current) {
      try {
        abortRef.current.abort();
      } catch (_) {}
      abortRef.current = null;
    }
  }, [clearIntervalIfAny]);

  useEffect(() => {
    if (isLoggedIn && sourcesRef.current.length > 0) {
      if (pollingEnabled) {
        // Polling mode: start polling with intervals
        setTimeout(() => startPolling(), 1000);
      } else {
        // Session mode: fetch once per session
        if (!hasFetchedRef.current) {
          setTimeout(() => {
            refreshAlerts();
            hasFetchedRef.current = true;
          }, 1000);
        }
      }
    } else {
      stopPolling();
      consecutiveErrorsRef.current = 0;
      hasFetchedRef.current = false;
    }
    return () => {
      stopPolling();
    };
  }, [isLoggedIn, pollingEnabled, startPolling, stopPolling, refreshAlerts]);

  return { refreshAlerts, stopPolling };
};

export default useSystemAlertFetcher;

